import {
    Container,
} from 'pixi.js';
import { Emitter, EmitterConfig, PathParticle } from 'pixi-particles';
import { LoadContext, wait, GameTime, SoundEffect, DisposableGroup, Disposable } from '../../utils';
import { ExtPromise } from './ExtPromise';
import { PING_SFX } from '../../constants';
import { Game } from '../../game';

const SPEED = 500;
const CONFIG: EmitterConfig = {
    alpha: {
        list: [
            { value: 1, time: 0 },
            { value: 1, time: 1 }
        ]
    },
    scale: {
        list: [
            // grow bigger in the first half
            { value: 0.2, time: 0 },
            { value: 1, time: 0.5 },
            // immediately after the half way point, inverse scale to point the other way
            { value: -1, time: 0.501 },
            // then continue growing in size
            { value: -1.5, time: 1 }
        ]
    },
    color: {
        list: [
            { value: '#ffffff', time: 0 },
            // turn darker at the halfway point
            { value: '#666666', time: 0.5 },
            { value: '#666666', time: 1 }
        ],
        // do NOT tween colors
        isStepped: true
    },
    speed: {
        list: [
            { value: SPEED, time: 0 },
            // reverse direction at the half way point
            { value: -SPEED, time: 0.5 },
            { value: -SPEED, time: 1 }
        ],
        // do NOT tween speed
        isStepped: true
    },
    acceleration: {
        x: 0,
        y: 0
    },
    maxSpeed: 0,
    startRotation: {
        min: 0,
        max: 0
    },
    noRotation: false,
    rotationSpeed: {
        min: 0,
        max: 0
    },
    lifetime: {
        min: 1,
        max: 1
    },
    blendMode: 'normal',
    // emit a wave every <frequency> seconds for <emitterLifetime> seconds total, then keep
    // the particles going until they die out
    frequency: 0.1,
    emitterLifetime: 0.7,
    maxParticles: 500,
    pos: {
        x: 0,
        y: 0
    },
    addAtBack: false,
    spawnType: 'point',
    // important for PathParticle so that we can actually reverse direction (speed isn't supposed
    // to be negative)
    extraData: { path: '0' }
};

export class PingAnimation extends Container
{
    // private out: Sprite;
    // private return: Sprite;
    private emitter: Emitter;
    private sfx: SoundEffect;
    private allSfx: {[opt:string]: SoundEffect};
    private disposables: DisposableGroup;

    public static preload()
    {
        const loader = LoadContext.current;
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');
        for (const opt in PING_SFX)
        {
            loader.add(opt, `./assets/sfx/${PING_SFX[opt]}`);
        }
    }

    constructor(game: Game)
    {
        super();

        const loader = LoadContext.current;
        const tex = loader.getSpritesheet('GameAtlas').textures['ping_line'];
        game.pixi.renderer.plugins.prepare.upload(tex);
        this.emitter = new Emitter(this, [tex], CONFIG);
        // path particle does movement differently, so negative speed works the way we want
        // even though we have no path
        this.emitter.particleConstructor = PathParticle;
        // this.emitter.autoUpdate = true;

        this.disposables = new DisposableGroup();
        this.allSfx = {};
        for (const opt in PING_SFX)
        {
            const sfx = new SoundEffect(loader.getAsset(opt), game);
            this.allSfx[opt] = sfx;
            this.disposables.add(sfx);
        }
        const sfxType = game.settings.sfxType;
        this.sfx = this.allSfx[sfxType.value];
        this.disposables.add(Disposable.fromPropertySubscription(sfxType, (value) => {
            this.sfx = this.allSfx[value];
        }));

        this.disposables.add(Disposable.fromPropertySubscription(GameTime.gameTick, this.update.bind(this)));
    }

    public reposition(x: number, y: number, angle: number)
    {
        this.emitter.updateOwnerPos(x, y);
        this.emitter.resetPositionTracking();
        this.emitter.rotate(angle);
    }

    public play(length: number, width: number)
    {
        // TODO: Position container based on grid, then move emitter based on ping source
        // modify lifetime to cover out & back for the specified distance
        this.emitter.minLifetime = this.emitter.maxLifetime = (length / SPEED) * 2;

        const back = new ExtPromise<void>();

        this.emitter.playOnce(() => back.resolve());

        this.sfx.play();

        // wait for the waves to reach the target for the first promise (include frequency for
        // initial spawn)
        return [wait((length / SPEED + 0.1) * 1000), back.promise];
    }

    public stop()
    {
        this.emitter.emit = false;
        this.emitter.cleanup();
    }

    private update(elapsedTime: number)
    {
        this.emitter?.update(elapsedTime / 1000);
    }

    public destroy()
    {
        this.disposables.dispose();
        this.emitter.destroy();
        super.destroy({children: true});
    }
}